import debounce from 'lodash/debounce';
import { ChangeEvent, FC, Ref, useEffect, useRef, useState } from 'react';

import { DEFAULT_DEBOUNCE_TIME } from '@constants/index';
import {
  StyledExitIcon,
  StyledInput,
  StyledInputContainer,
  StyledSearchBoxContainer,
  StyledSearchIcon,
} from './SearchBar.styles';
import { SearchBarProps } from './SearchBar.types';

const SearchBar: FC<SearchBarProps> = ({ onChange, onClear, debounceTime = DEFAULT_DEBOUNCE_TIME, ...props }) => {
  const { isSensitive = false } = props;
  const [isInputDisplayed, setIsInputDisplayed] = useState<boolean>(false);
  const [isClearDisplayed, setIsClearDisplayed] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const inputRef: Ref<HTMLInputElement> = useRef(null);

  const debouncedChange = useRef(
    debounce(async (e) => {
      onChange?.(e);
    }, debounceTime),
  ).current;

  const handleSearchIconClick = (): void => {
    setIsInputDisplayed((val: boolean): boolean => {
      return !val;
    });
  };

  const handleExitIconClick = (): void => {
    setIsClearDisplayed((val: boolean): boolean => {
      return !val;
    });
    setInputValue('');
    setIsInputDisplayed(false);
    onClear?.();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e?.target?.value || '';

    setInputValue(value);
    setIsClearDisplayed(value.length > 0);
    void debouncedChange(e);
  };

  useEffect(() => {
    return () => {
      debouncedChange.cancel();
    };
  }, [debouncedChange]);

  useEffect(() => {
    if (isInputDisplayed) {
      inputRef?.current?.focus();
    }
  }, [isInputDisplayed]);

  return (
    <StyledSearchBoxContainer>
      {!isInputDisplayed && <StyledSearchIcon data-testid="search-bar-search-icon" onClick={handleSearchIconClick} />}
      {isInputDisplayed && (
        <StyledInputContainer>
          <StyledInput
            {...props}
            onChange={handleInputChange}
            ref={inputRef}
            role="search"
            testId="search-bar-input"
            value={inputValue}
            className={isSensitive ? 'rb-mask' : ''}
          />
          {isClearDisplayed && <StyledExitIcon data-testid="search-bar-exit-icon" onClick={handleExitIconClick} />}
        </StyledInputContainer>
      )}
    </StyledSearchBoxContainer>
  );
};

export default SearchBar;
